import BillingRedirectLoader from "./pages/Billing/BillingRedirectLoader";
import React, { useEffect, useRef, useState, Suspense } from "react";
import ClubFreeAccessAnnouncement from "./Components/ClubFreeAccessAnnouncement";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuthContext } from "./context/AuthContext";

import {
  Bell,
  Building2,
  CalendarDays,
  ClipboardList,
  Folder,
  Globe,
  IdCard,
  LayoutDashboard,
  LifeBuoy,
  Megaphone,
  Receipt,
  Server,
  Settings2,
  Store,
  UserCog,
  UserSearch,
  Users,
} from "lucide-react";
import { getSeasonYear, isClubAdmin, isSuperAdmin } from "./utility/Utils";
import { useTranslation } from "./context/Internationalization";

import Loader from "./Components/Loader";
import TopBar from "./Components/TopBar/TopBar";
import ClubsDrawer from "./pages/SuperAdmin/ClubsTable/ClubsDrawer";
import AppSidebar from "./Components/AppSidebar/AppSidebar";
import { SidebarProvider } from "./shadcn/components/ui/sidebar";
import { GET_USER } from "./graphql/userGraphql";
import { selectSessionClub } from "./auth/consoleSession";
import { clearCookies, clearStorages, getStorage, signOut } from "./auth/utils";
import { useInvitations } from "./context/InvitationContext";
import { GET_CLUB_TEAMS } from "./graphql/postGraphql";
import LogoutConfirmationModal from "./Components/LogoutConfirmationModal";
import ClubChooseConfirmationModal from "./Components/ClubChooseConfirmationModal";
import SmsLoginEmailPromptModal from "./Components/SmsLoginEmailPromptModal";
import MobileChangeModal from "./Components/MobileChangeModal";
import ContactPhoneModal from "./Components/ContactPhoneModal";
import UserPhotoModal from "./Components/UserPhotoModal";
import { useAppState } from "./context/AppContext";
import { AccountActionsContext } from "./context/AccountActionsContext";
import useGraphql from "./Hooks/useGraphql";
import useLog from "./Hooks/useLog";
import useClubBillingStatus from "./Hooks/useClubBillingStatus";
import useIntercomMessenger from "./Hooks/useIntercomMessenger";
import usePageTopOffset from "./Hooks/usePageTopOffset";
import { canViewClubBilling } from "./context/BillingGateContext";
import { canManageStaff } from "./pages/Staff/staffUtils";
import { USER_ROLES } from "./utility/Enum";
import {
  ACCOUNT_SETTINGS_ROUTE,
  BILLING_ROUTE,
  FILES_ROUTE,
  FORMS_ROUTE,
  HOME_ROUTE,
  MANAGER_HOME_ROUTE,
  NOTIFICATIONS_ROUTE,
  PLAYERS_ROUTE,
  POSTS_ROUTE,
  SCHEDULE_ROUTE,
  SHOP_ROUTE,
  STAFF_ROUTE,
  SUPER_ADMIN_CLUBS_ROUTE,
  SUPER_ADMIN_IMPERSONATE_ROUTE,
  SUPER_ADMIN_ROLES_ROUTE,
  SUPER_ADMIN_HELP_LINKS_ROUTE,
  SUPER_ADMIN_DOMAIN_REQUESTS_ROUTE,
  WEBSITE_ROUTE,
  isWebsiteEnabled,
} from "./routes";
import { toast } from "sonner";


// The mobile top bar opens the shadcn sidebar sheet.
const SidebarTopBar = (props) => <TopBar {...props} />;

// The shadcn sidebar saves open/closed in this cookie; start from it so the
// desktop sidebar stays the way the user left it. Open by default.
const sidebarStartsOpen = () =>
  !document.cookie.split("; ").includes("sidebar_state=false");

const AppShell = ({ children }) => {
  const pageTopOffset = usePageTopOffset();
  const [logoutModal, setLogoutModal] = useState(false);
  const [chooseClubModal, setChooseClubModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [contactPhoneModalOpen, setContactPhoneModalOpen] = useState(false);
  const [userPhotoModalOpen, setUserPhotoModalOpen] = useState(false);
  const [clubSettingsScope, setClubSettingsScope] = useState(null);
  const { t, switchLanguage, lang } = useTranslation();
  const auth = useAuthContext();
  const appState = useAppState();
  const { pathname } = useLocation();

  const user = auth?.authState?.user;
  const impersonating = Boolean(getStorage().getItem("impersonate"));
  const club = user?.club;
  const clubId = club?._id || "";
  useIntercomMessenger({ userId: user?._id, clubId, impersonating, language: lang });
  const seasonStartYear = getSeasonYear();
  const season = `${seasonStartYear}/${String(seasonStartYear + 1).slice(-2)}`;
  const isUserSuperAdmin = user ? isSuperAdmin(user) : false;
  const isUserClubAdmin = user ? isClubAdmin(user) : false;
  const requestGeneration = useRef(0);
  const { invitations } = useInvitations();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const {
    state: billingGateState,
    loadBillingStatus,
    resetBilling,
  } = useClubBillingStatus();

  const captureRequestContext = () => {
    const generation = requestGeneration.current;
    const impersonatedUserId = () => {
      try { return JSON.parse(getStorage().getItem("impersonate"))?._id || null; }
      catch (_) { return null; }
    };
    const impersonation = impersonatedUserId();
    return () => generation === requestGeneration.current &&
      impersonation === impersonatedUserId();
  };

  const impersonateProc = () => {
    const impersonate = getStorage().getItem("impersonate");
    if (impersonate) {
      getStorage().setItem("impersonate", JSON.stringify(user));
    } else {
      getStorage().setItem("user", JSON.stringify(user));
    }
  };

  const getClubTeams = () => {
    if (!clubId) return;
    const isCurrent = captureRequestContext();

    axiosGraphQL({
      query: GET_CLUB_TEAMS,
      variables: {
        clubId,
        skip: 0,
        teamsLimit: 500,
      },
    })
      .then((response) => {
        if (!isCurrent()) return;
        const clubTeams = response.data.data.ClubTeams;
        appState.setClubTeams(clubTeams);
        return response.data.data.ClubTeams;
      })
      .catch((error) => {
        if (!isCurrent()) return;
        errorLog(error, "AppShell", "getClubTeams", "GET_CLUB_TEAMS");
        toast.error(error.message);
      });
  };

  const getUser = () => {
    const isCurrent = captureRequestContext();
    return axiosGraphQL({ query: GET_USER })
      .then(async (response) => {
        if (!isCurrent()) return;
        const user = response.data.data.me?.user;
        if (!user) {
          auth.logout();
          return;
        }

        const userLangDB = user.language;
        const userLangLS = getStorage().getItem("language");
        if (userLangDB !== userLangLS) {
          switchLanguage(userLangDB);
        }
        Object.assign(user, selectSessionClub(response.data.data.me, clubId));
        setSelectedClub(user.club);
        getStorage().setItem("activeClubId", String(user.club?._id || ""));

        auth.setAuthState({ user, token: getStorage().getItem("token") });

        const isValidRole =
          user.roles.includes(USER_ROLES.CLUB_ADMIN) ||
          user.roles.includes(USER_ROLES.SUPER_ADMIN);
        if (!isValidRole) {
          auth.logout();
        }
      })
      .catch((error) => {
        if (!isCurrent()) return;
        errorLog(error, "AppShell", "getUser", "GET_USER");
        toast.error(error.message);
        throw error;
      });
  };

  useEffect(() => {
    let logoutTimer;
    setSelectedClub(user?.club || null);
    appState.setClubTeams([]);
    if (!clubId && !isUserSuperAdmin) {
      toast.error(t("your_account_does_not_belong_to_a_club"));
      logoutTimer = setTimeout(() => {
        handleLogout();
      }, 4000);
    } else {
      if (clubId) getClubTeams();
      getUser().catch(() => undefined);
    }
    return () => {
      // Club switching and impersonation must invalidate old requests/timers.
      requestGeneration.current += 1;
      clearTimeout(logoutTimer);
    };
  }, [clubId, user?._id, isUserSuperAdmin, impersonating]);

  useEffect(() => {
    if (!clubId) {
      resetBilling();
      return;
    }

    loadBillingStatus(String(clubId));
  }, [clubId, loadBillingStatus, resetBilling]);

  const canOpenClubSettings = Boolean(
    clubId && (isUserClubAdmin || isUserSuperAdmin)
  );
  const clubSettingsInScope =
    canOpenClubSettings &&
    clubSettingsScope?.clubId === clubId &&
    clubSettingsScope?.userId === user?._id &&
    clubSettingsScope?.impersonating === impersonating;
  const clubSettingsOpen = clubSettingsInScope && !clubSettingsScope.closing;

  const closeClubSettings = () => {
    setClubSettingsScope((scope) => scope && { ...scope, closing: true });
  };
  const handleClubSettingsExited = () => {
    setClubSettingsScope((scope) => (scope?.closing ? null : scope));
  };

  useEffect(() => {
    setClubSettingsScope(null);
  }, [clubId, user?._id, impersonating, canOpenClubSettings]);

  const menuLinks = (t) =>
    [
      {
        label: t("club"),
        path: HOME_ROUTE,
        icon: LayoutDashboard,
      },
      {
        label: t("schedule_title"),
        path: SCHEDULE_ROUTE,
        icon: CalendarDays,
      },
      isUserClubAdmin && {
        label: t("players_page_title"),
        path: PLAYERS_ROUTE,
        icon: Users,
      },
      {
        label: t("home"),
        path: POSTS_ROUTE,
        icon: Megaphone,
      },
      {
        label: t("files"),
        path: FILES_ROUTE,
        icon: Folder,
      },
      {
        label: t("forms"),
        path: FORMS_ROUTE,
        icon: ClipboardList,
      },
      {
        label: t("shop"),
        path: SHOP_ROUTE,
        icon: Store,
      },
      canManageStaff(user) && {
        label: t("staff"),
        path: STAFF_ROUTE,
        icon: IdCard,
      },
      isUserClubAdmin && isWebsiteEnabled() && {
        label: t("website"),
        path: WEBSITE_ROUTE,
        icon: Globe,
      },
    ]
      .filter(Boolean);

  const secondMenuLinks = (t) =>
    [
      canViewClubBilling(billingGateState, clubId) && {
        label: t("billing"),
        path: BILLING_ROUTE,
        icon: Receipt,
      },
      canOpenClubSettings && {
        label: t("settings"),
        drawerOpened: clubSettingsOpen,
        onClick: () =>
          setClubSettingsScope({ clubId, userId: user._id, impersonating }),
        icon: Settings2,
      },
      clubId && {
        label: t("notifications"),
        path: NOTIFICATIONS_ROUTE,
        badge: invitations?.length,
        icon: Bell,
        hideOnMobile: true,
      },
    ]
      .filter(Boolean);

  let superAdminMenuLinks = (t) => {
    return [
      {
        label: t("clubs_admin"),
        path: SUPER_ADMIN_CLUBS_ROUTE,
        icon: Building2,
      },
      {
        label: `${t("search_user")}`,
        path: SUPER_ADMIN_IMPERSONATE_ROUTE,
        icon: UserSearch,
      },
      {
        label: `${t("roles_admin")}`,
        path: SUPER_ADMIN_ROLES_ROUTE,
        icon: UserCog,
      },
      {
        label: t("page_help_settings_title"),
        path: SUPER_ADMIN_HELP_LINKS_ROUTE,
        icon: LifeBuoy,
      },
      {
        label: `${t("domain_requests_admin")}`,
        path: SUPER_ADMIN_DOMAIN_REQUESTS_ROUTE,
        icon: Server,
      },
    ];
  };

  const handleLogout = async () => {
    // shutdown Intercom
    if (typeof Intercom === "function") {
      Intercom("shutdown");
    }

    try {
      // Sign out from Firebase
      await signOut();

      // clear lS and SS
      clearStorages();

      // clear cookies
      clearCookies();

      // Perform logout via auth context
      auth.logout();
    } catch (error) {
      toast.error(t("error_during_logout"));
    }
  };

  useEffect(() => {
    // Restored phone sessions may predate the lastLoginMethod storage flag.
    const shouldPrompt = Boolean(
      user?._id &&
      user?.mobile &&
      !user?.email &&
      !impersonating &&
      Number(localStorage.getItem("emailPromptRemindAfter") || 0) <= Date.now()
    );
    setEmailModalOpen(shouldPrompt);
  }, [user?._id, user?.mobile, user?.email, impersonating]);

  const navigate = useNavigate();

  const openAccountSettings = () => {
    navigate(ACCOUNT_SETTINGS_ROUTE);
  };

  const handleChangeSelectClub = async (selectedClubId) => {
    setSelectedClub(user.clubs.find((club) => club?._id === selectedClubId));
    setChooseClubModal(true);
  };

  const updateSelectedClub = async () => {
    const clubToBeSelected = user.clubs.find(
      (club) => club?._id === selectedClub?._id
    );
    user.club = clubToBeSelected;

    auth.setAuthState({
      user: {
        ...user,
        club: selectedClub,
      },
      token: getStorage().getItem("token"),
    });

    getStorage().setItem("activeClubId", String(selectedClub?._id || ""));

    impersonateProc();

    setChooseClubModal(false);
    navigate(MANAGER_HOME_ROUTE);
  };

  const onCancelSelectClub = () => {
    setSelectedClub(user.club);
    setChooseClubModal(false);
  };

  const onLogout = () => {
    setLogoutModal(true);
  };

  return (
    <SidebarProvider
      defaultOpen={sidebarStartsOpen()}
      style={{ "--page-top-offset": `${pageTopOffset}px` }}
    >
      <SidebarTopBar
        handleLogout={onLogout}
        invitations={invitations?.length}
        user={user}
      />
      <AppSidebar
        club={club}
        clubs={user?.clubs || []}
        selectedClubId={selectedClub?._id}
        season={season}
        onClubChange={handleChangeSelectClub}
        mainItems={clubId ? menuLinks(t) : []}
        superAdminItems={isUserSuperAdmin ? superAdminMenuLinks(t) : []}
        footerItems={secondMenuLinks(t)}
        toggleLabel={t("sidebar_toggle")}
        pathname={pathname}
        user={user}
        showAccountSettings={!impersonating}
        onOpenAccountSettings={openAccountSettings}
        onLogout={onLogout}
      />
      <ClubChooseConfirmationModal
        open={chooseClubModal}
        clubName={selectedClub?.name}
        onClose={onCancelSelectClub}
        onSave={updateSelectedClub}
      />
      <LogoutConfirmationModal
        logout={handleLogout}
        open={logoutModal}
        onClose={() => setLogoutModal(false)}
      />

      {clubSettingsInScope && (
        <ClubsDrawer
          key={clubId}
          openDrawer={clubSettingsOpen}
          onExited={handleClubSettingsExited}
          selectedClub={club}
          title={t("club_settings")}
          onCloseDrawer={closeClubSettings}
          getClubs={() => globalThis.location.reload()}
        />
      )}

      <AccountActionsContext.Provider
        value={{
          openEmailEditor: () => setEmailModalOpen(true),
          openLoginMobileEditor: () => {
            if (!user?.email) setMobileModalOpen(true);
          },
          openContactPhoneEditor: () => setContactPhoneModalOpen(true),
          openPhotoEditor: () => setUserPhotoModalOpen(true),
        }}
      >
        {/* 768px matches the shadcn sidebar breakpoint: the top bar only
            shows while the sidebar is a mobile sheet. */}
        <div className="min-w-0 flex-1 pb-14 pt-[90px] md:pt-0">
          <ClubFreeAccessAnnouncement user={user} billingState={billingGateState} onRefreshBilling={loadBillingStatus} pathname={pathname} />
          <Suspense fallback={pathname === BILLING_ROUTE || pathname.startsWith(BILLING_ROUTE + "/")
              ? <BillingRedirectLoader message={t("loading")} /> : <Loader />}>{children}</Suspense>
        </div>
      </AccountActionsContext.Provider>
      <SmsLoginEmailPromptModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        user={user}
        language={lang}
      />
      <MobileChangeModal
        open={mobileModalOpen}
        onClose={() => setMobileModalOpen(false)}
        user={user}
        axiosGraphQL={axiosGraphQL}
        onCompleted={({ user: refreshedUser, token }) =>
          auth.setAuthState({ user: refreshedUser, token })
        }
      />
      <ContactPhoneModal
        open={contactPhoneModalOpen}
        onClose={() => setContactPhoneModalOpen(false)}
        user={user}
        axiosGraphQL={axiosGraphQL}
        onCompleted={getUser}
      />
      <UserPhotoModal
        open={userPhotoModalOpen}
        onClose={() => setUserPhotoModalOpen(false)}
        axiosGraphQL={axiosGraphQL}
        onCompleted={getUser}
      />
    </SidebarProvider>
  );
};

export default AppShell;
